/* eslint-disable @next/next/no-img-element */

import { memo } from 'react';
import SiteAccordion from 'components/site/site-accordion';
import NextImage from 'next/image';
import Url from '../../lib/siteurls';
import { createGlobalStyle } from 'styled-components';
import { mqLargeMin } from 'styled/tools/media-queries';

const NewStandardStyles = createGlobalStyle`
  .pds08-new-standard {
  max-width: none !important;
  padding: 0 !important;
  background-color: var(--fill-primary);

  > .container {
    max-width: 1440px;
    padding: 60px 30px 30px 30px;
    margin: 0 auto;

    @media ${mqLargeMin} {
      padding: 80px 50px 80px 50px;
    }

    > * {
      color: var(--text-primary);
    }
  }

  h2 {
    margin-bottom: 30px;

    span {
      display: block;
      font-size: 36px;
      font-weight: 300;
      color: var(--text-primary);
      line-height: 1.1;
    }

    @media ${mqLargeMin} {
      margin-bottom: 40px;

      span {
        font-size: 50px;
      }
    }
  }

  .mobile-cluster {
    @media ${mqLargeMin} {
      display: none;
    }

    .image-group {
      display: flex;
      flex-wrap: wrap;
      margin-bottom: 35px;

      .image-container {
        position: relative;
        width: 50%;
        transition: transform 500ms ease-in-out;

        &:after {
          content: '';
          display: block;
          padding-bottom: 100%;
        }

        img {
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 50%;
        }

        &:nth-of-type(1) {
          z-index: 4;
          transform: translate(50%, 50%);
        }

        &:nth-of-type(2) {
          z-index: 3;
          transform: translate(-50%, 50%);
        }

        &:nth-of-type(3) {
          z-index: 2;
          transform: translate(50%, -50%);
        }

        &:nth-of-type(4) {
          z-index: 1;
          transform: translate(-50%, -50%);
        }
      }
    }

    &.animate {
      .image-container {
        transform: translate(0, 0) !important;
      }
    }

    .site-accordion {
      * {
        color: var(--text-primary);
      }

      .container {
        background-color: var(--fill-primary);
      }
    }
  }

  .desktop-cluster {
    display: none;
    gap: 50px;

    @media ${mqLargeMin} {
      display: flex;
    }

    .cluster-item {
      position: relative;
      width: 25%;

      > * {
        color: var(--text-primary);
      }

      p {
        margin-top: 1em;
      }

      .image-container {
        position: relative;
        left: -25px;
        margin-bottom: 35px;
        width: calc(100% + 50px);
        opacity: 0;
        transition:
          transform 500ms ease-in-out,
          opacity 330ms ease-in-out;

        &:after {
          content: '';
          display: block;
          padding-bottom: 100%;
        }

        img {
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 50%;
        }
      }

      &:nth-of-type(1) {
        .image-container {
          z-index: 4;
        }
      }

      &:nth-of-type(2) {
        .image-container {
          z-index: 3;
          transform: translate(-50%, 0);
          transition-delay: 100ms;
        }
      }

      &:nth-of-type(3) {
        .image-container {
          z-index: 2;
          transform: translate(-110%, 0);
          transition-delay: 200ms;
        }
      }

      &:nth-of-type(4) {
        .image-container {
          z-index: 1;
          transform: translate(-170%, 0);
          transition-delay: 300ms;
        }
      }
    }

    &.animate {
      .image-container {
        transform: translate(0, 0) !important;
        opacity: 1;
      }
    }
  }
}

`;

const PDS08NewStandard = () => {
  const content = [
    {
      img: `${Url.cloudinary}/f_auto,q_auto/v1649199895/PDS%20PDP/newstandard1.png`,
      title: 'Biofermentation',
      desc: 'How bacteria are cultured impacts their viability. We optimize our biofermentation for pH, temperature, and 28 other critical growth factors. Our mediums contain no meat or dairy.',
    },
    {
      img: `${Url.cloudinary}/f_auto,q_auto/v1649729399/PDS%20PDP/Survivability.png`,
      title: 'Survivability',
      desc: '7 of the 9 strains are microencapsulated with a lipid coating to further protect from stomach acids, digestive enzymes and bile salts during digestion. This also means that refrigeration is not required.',
    },
    {
      img: `${Url.cloudinary}/f_auto,q_auto/v1649199895/PDS%20PDP/newstandard3.png`,
      title: 'Testing',
      desc: 'We do extensive lot-to-lot testing at each step and on the final product. With 50+ QA/QC checkpoints, we monitor purity and strength under both US and EU protocols, including cGMP + HACCP.',
    },
    {
      img: `${Url.cloudinary}/f_auto,q_auto/v1649729399/PDS%20PDP/Clinically-derived-dosages.png`,
      title: 'Clinically Derived Dosages',
      desc: 'All ingredients and their included dosages have been specifically studied in a pediatric population.',
    },
  ];

  return (
    <section className='pds08-new-standard'>
      <NewStandardStyles />
      <div className='container'>
        <h2>
          <span>A new standard</span>
          <span>in probiotics.</span>
        </h2>
        <div className={`mobile-cluster animate`}>
          <div className='image-group'>
            {content.map((item, i) => (
              <div key={`image-${i}`} className='image-container'>
                <img src={item.img} alt='' loading='lazy' />
              </div>
            ))}
          </div>
          {content.map((item, i) => (
            <SiteAccordion key={`accrodion-${i}`} index={i} title={item.title}>
              {item.desc}
            </SiteAccordion>
          ))}
        </div>
        <div className={`desktop-cluster animate`}>
          {content.map((item, i) => (
            <div key={`cluster-item-${i}`} className='cluster-item'>
              <div className='image-container'>
                <NextImage
                  src={item.img}
                  alt={item.title || ''}
                  width={350}
                  height={350}
                />
              </div>
              <strong>{item.title}</strong>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default memo(PDS08NewStandard);
